/** @format */

import {useMemo} from 'react';

import {
  addYears,
  differenceInDays,
  endOfMonth,
  format,
  isAfter,
  isBefore,
  isEqual,
  startOfMonth,
  subDays,
} from 'date-fns';
import {ru} from 'date-fns/locale';

import {getPeriods} from 'pages/Home/Project/Steps/Periods/components/ChartPeriods/utils';

import {useBudgetWidgetsData} from '../../../hooks/BudgetWidgetsDataContext';
import {useGetGraphStatsQuery} from '../graphql/__generated__';
import {PredictionData, Status} from '../interfaces';

interface UsePredictionReturn {
  data: PredictionData | null;
  loading: boolean;
  error: any;
}

export const usePrediction = (): UsePredictionReturn => {
  const budgetWidgetsData = useBudgetWidgetsData();
  const contextCompany = budgetWidgetsData?.data?.currentUser?.currentCompany;
  const contextMediaplans = contextCompany?.unfinishedMediaplans;
  const useContextMediaplans = Boolean(
    !budgetWidgetsData?.loading && Array.isArray(contextMediaplans),
  );
  const yesterday = useMemo(() => {
    const today = new Date();
    const todayString = format(today, 'yyyy-MM-dd');
    const yesterdayDate = subDays(new Date(todayString), 1);
    return yesterdayDate;
  }, []);

  const mediaplansLoading = Boolean(budgetWidgetsData?.loading) && !useContextMediaplans;
  const mediaplansError = budgetWidgetsData?.error;

  const mediaplan = useMemo(() => {
    const mediaplans = contextMediaplans || [];

    return mediaplans.find((plan: any) => {
      const startDate = new Date(plan.startOn);
      const endDate = new Date(plan.endOn);

      if (plan.type === 'REPEATABLE') {
        return isBefore(startDate, yesterday) || isEqual(startDate, yesterday);
      }

      return (
        (isAfter(yesterday, startDate) || isEqual(yesterday, startDate)) &&
        (isBefore(yesterday, endDate) || isEqual(yesterday, endDate))
      );
    });
  }, [contextMediaplans, yesterday]);

  const {periodStart, periodEnd, targetDate} = useMemo(() => {
    if (mediaplan) {
      const startDate = new Date(mediaplan.startOn);
      const startDateNextYear = addYears(startDate, 1);

      const result = getPeriods({
        startDate,
        endDate: startDateNextYear,
        repeatEvery: mediaplan.repeat === 'WEEKLY' ? 'weekly' : 'monthly',
        isLinkedWithPeriodStart: mediaplan.startOnPeriodBegin ?? false,
        budgetPlan: mediaplan.budget,
      });

      const [nearestPeriod] = result;

      let endDate;

      if (mediaplan.endOn) {
        endDate = new Date(mediaplan.endOn);
      } else if (nearestPeriod && nearestPeriod.end) {
        endDate = nearestPeriod.end;
      } else {
        endDate = yesterday;
      }

      return {
        periodStart: startDate,
        periodEnd: yesterday,
        targetDate: endDate,
      };
    } else {
      // Без медиаплана - до конца месяца
      const monthStart = startOfMonth(yesterday);
      const monthEnd = endOfMonth(yesterday);
      const daysFromStart = differenceInDays(yesterday, monthStart) + 1;

      const calculationStart = daysFromStart < 7 ? subDays(yesterday, 6) : monthStart;

      return {
        periodStart: calculationStart,
        periodEnd: yesterday,
        targetDate: monthEnd,
      };
    }
  }, [mediaplan, yesterday]);

  const {
    data: statsData,
    loading: statsLoading,
    error: statsError,
  } = useGetGraphStatsQuery({
    variables: {
      startDate: format(periodStart, 'yyyy-MM-dd'),
      endDate: format(periodEnd, 'yyyy-MM-dd'),
    },
    skip: !periodStart || !periodEnd,
  });

  const data = useMemo((): PredictionData | null => {
    if (mediaplansLoading || statsLoading || mediaplansError || statsError) {
      return null;
    }

    const stats = statsData?.currentUser?.currentCompany?.graphStats || [];

    // Группируем данные по дням и суммируем расходы за каждый день
    const dailySpends = stats
      .filter(stat => stat.extendedPlatform === stat.platform)
      .reduce(
        (acc, day) => {
          if (!acc[day.date]) {
            acc[day.date] = 0;
          }
          acc[day.date] += day.spent || 0;
          return acc;
        },
        {} as Record<string, number>,
      );

    const dailySpendValues = Object.values(dailySpends) as number[];
    const actualDaysWithData = dailySpendValues.length;

    // Расчет среднесуточного расхода (μ)
    const totalSpent = dailySpendValues.reduce((sum, daySpent) => sum + daySpent, 0);
    const avgDailySpent = actualDaysWithData > 0 ? totalSpent / actualDaysWithData : 0;

    // Расчет среднесуточного отклонения (σ)
    // σ = √(Σ(Xi - μ)² / n)
    const variance =
      dailySpendValues.reduce((sum: number, daySpent: number) => {
        const deviation = daySpent - avgDailySpent;
        return sum + Math.pow(deviation, 2);
      }, 0) / actualDaysWithData;

    const standardDeviation = Math.sqrt(variance);

    const futurePeriodDays = differenceInDays(targetDate, yesterday);

    // Формулы прогнозного бюджета:
    const minPrediction = (avgDailySpent - standardDeviation) * futurePeriodDays; // Bmin
    const maxPrediction = (avgDailySpent + standardDeviation) * futurePeriodDays; // Bmax
    const midPrediction = (minPrediction + maxPrediction) / 2; // Bmid

    // Текущий общий расход + прогноз
    const currentTotalSpent = totalSpent;
    const finalPrediction = currentTotalSpent + midPrediction;

    // Сравнение с планом
    const plan = mediaplan ? mediaplan.budget : currentTotalSpent;

    const difference = finalPrediction - plan;
    const percentageDeviation = plan > 0 ? (Math.abs(difference) / plan) * 100 : 0;

    let status: Status = 'success';

    if (percentageDeviation > 30) {
      status = 'error';
    } else if (percentageDeviation > 2) {
      status = 'warning';
    }

    return {
      status,
      prediction: Math.round(finalPrediction),
      planTo: Math.round(maxPrediction + currentTotalSpent),
      planFrom: Math.round(minPrediction + currentTotalSpent),
      difference: Math.round(difference),
      targetDateFormatted: format(targetDate, 'dd MMMM', {locale: ru}),
      currency: stats[0]?.currency || '₽',
      percentageDeviation,
      isOverspent: finalPrediction > plan,
    };
  }, [
    mediaplansLoading,
    statsLoading,
    mediaplansError,
    statsError,
    statsData?.currentUser?.currentCompany?.graphStats,
    periodEnd,
    periodStart,
    targetDate,
    yesterday,
    mediaplan,
  ]);

  return {
    data,
    loading: mediaplansLoading || statsLoading,
    error: mediaplansError || statsError,
  };
};
